import { useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'framer-motion';
import { api } from '@/lib/api';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';

interface TurnstileApi {
  render: (
    el: HTMLElement,
    opts: {
      sitekey: string;
      callback: (token: string) => void;
      'expired-callback': () => void;
      theme: 'auto' | 'light' | 'dark';
      size: 'normal' | 'compact' | 'flexible';
    }
  ) => string | undefined;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

/**
 * `@marsidev/react-turnstile` (a transitive dep) already declares
 * `window.turnstile` globally, but its type predates the `flexible` size — so
 * read the global through our own accurate shape rather than redeclaring it.
 */
function getTurnstile(): TurnstileApi | undefined {
  return (window as unknown as { turnstile?: TurnstileApi }).turnstile;
}

/**
 * The one and only waitlist form on the page — kept single so there is exactly
 * one Turnstile widget to manage. Every CTA scrolls here and focuses it.
 */
export function WaitlistForm() {
  const [email, setEmail] = useState('');
  const [xHandle, setXHandle] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const sectionRef = useRef<HTMLDivElement>(null);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  // Only load the captcha once the form is near the viewport.
  const inView = useInView(sectionRef, { once: true, margin: '300px 0px' });

  const renderTurnstile = useCallback(() => {
    if (!TURNSTILE_SITE_KEY || !turnstileRef.current) return;
    const turnstile = getTurnstile();
    if (!turnstile || widgetIdRef.current != null) return;

    widgetIdRef.current =
      turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token: string) => setTurnstileToken(token),
        'expired-callback': () => setTurnstileToken(''),
        theme: 'dark',
        size: 'flexible',
      }) ?? null;
  }, []);

  useEffect(() => {
    if (!inView || !TURNSTILE_SITE_KEY) return;

    if (!document.querySelector('script[src*="turnstile"]')) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      document.head.appendChild(script);
    }

    // The script may already be in flight from a previous mount, in which case
    // its load event has been and gone — poll rather than listen.
    if (getTurnstile()) {
      renderTurnstile();
      return;
    }

    let tries = 0;
    const timer = window.setInterval(() => {
      if (getTurnstile()) {
        renderTurnstile();
        window.clearInterval(timer);
      } else if (++tries > 100) {
        window.clearInterval(timer);
      }
    }, 100);

    return () => window.clearInterval(timer);
  }, [inView, renderTurnstile]);

  // Tear the widget down so a remount can render a fresh one.
  useEffect(() => {
    return () => {
      const turnstile = getTurnstile();
      if (turnstile && widgetIdRef.current != null) {
        try {
          turnstile.remove(widgetIdRef.current);
        } catch {
          /* widget already gone */
        }
        widgetIdRef.current = null;
      }
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setError('Please complete the captcha');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.joinWaitlist(
        email.trim(),
        xHandle.trim() || undefined,
        turnstileToken || undefined
      );
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      const turnstile = getTurnstile();
      if (turnstile && widgetIdRef.current != null) {
        turnstile.reset(widgetIdRef.current);
        setTurnstileToken('');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        ref={sectionRef}
        className="p-7 sm:p-9"
        style={{ border: '1px solid var(--acid)', background: 'var(--acid-wash)' }}
      >
        <div className="flex items-center gap-2.5">
          <span className="pu-dot" />
          <span className="pu-label" style={{ color: 'var(--acid)' }}>
            Position confirmed
          </span>
        </div>
        <p
          className="pu-display mt-4 text-[clamp(28px,4.2vw,44px)]"
          style={{ color: 'var(--ink)' }}
        >
          You&rsquo;re on the list.
        </p>
        <p className="pu-body mt-3 max-w-md">
          We release invites in batches. Yours lands in your inbox with a code that unlocks the app
          — nothing else, ever.
        </p>
      </div>
    );
  }

  return (
    <div ref={sectionRef}>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-[1.4fr_1fr]">
          <div>
            <label className="pu-label mb-2.5 block" htmlFor="waitlist-email">
              Email <span style={{ color: 'var(--acid)' }}>*</span>
            </label>
            <input
              id="waitlist-email"
              type="email"
              className="pu-input"
              placeholder="you@domain.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError('');
              }}
              disabled={isSubmitting}
              required
            />
          </div>

          <div>
            <label className="pu-label mb-2.5 block" htmlFor="waitlist-x">
              X handle <span style={{ color: 'var(--ink-3)' }}>(optional)</span>
            </label>
            <input
              id="waitlist-x"
              type="text"
              className="pu-input"
              placeholder="@handle"
              value={xHandle}
              onChange={(e) => setXHandle(e.target.value)}
              disabled={isSubmitting}
            />
          </div>
        </div>

        {TURNSTILE_SITE_KEY && <div ref={turnstileRef} className="pt-1" />}

        <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:items-center">
          <button type="submit" className="pu-btn pu-btn--acid" disabled={isSubmitting}>
            <span>{isSubmitting ? 'Submitting' : 'Claim your spot'}</span>
            <span aria-hidden>{isSubmitting ? '···' : '→'}</span>
          </button>
          <p className="pu-label" style={{ letterSpacing: '0.12em', lineHeight: 1.6 }}>
            One email. Your invite code. Nothing else.
          </p>
        </div>

        {error && (
          <p className="pu-label pt-1" style={{ color: 'var(--red)', letterSpacing: '0.1em' }}>
            ⚠ {error}
          </p>
        )}
      </form>
    </div>
  );
}
